import { formatINR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, Store } from "lucide-react";

export function MoneyField({ label, hint, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">₹</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          className="currency-input pl-7"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          data-testid={testId}
        />
      </div>
      {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );
}

const TONES = {
  default: "text-zinc-900",
  positive: "text-emerald-700",
  warning: "text-amber-700",
  danger: "text-rose-600",
};

export function Row({ label, value, sign = "", strong = false, tone = "default", testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500 text-xs">
        {sign && `${sign} `}
        {label}
      </span>
      <span className={`${strong ? "font-semibold" : "font-medium"} ${TONES[tone]}`} data-testid={testId}>
        {formatINR(value)}
      </span>
    </div>
  );
}

export function Kpi({ label, value, tone = "default", big = false, testId }) {
  return (
    <div className={`bg-white border ${big ? "border-emerald-200 shadow-sm" : "border-zinc-200"} rounded-xl p-4`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
        {big ? <TrendingUp className="h-3 w-3" /> : <Store className="h-3 w-3 opacity-50" />}
        {label}
      </div>
      <div
        className={`mt-1.5 font-display ${big ? "text-2xl md:text-3xl" : "text-lg"} font-semibold tabular ${TONES[tone]}`}
        data-testid={testId}
      >
        {formatINR(value || 0)}
      </div>
    </div>
  );
}

export const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 whitespace-nowrap ${className}`}>
    {children}
  </th>
);

export const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2.5 text-right whitespace-nowrap ${className}`}>{children}</td>
);

export const profitTone = (v) => (v >= 0 ? "text-emerald-700" : "text-rose-600");

// Shared money columns for weekly / history / branch tables
export const MONEY_COLS = [
  ["earning_for_shop", "Earning for Shop"],
  ["price_to_shop", "Price to Shop"],
  ["total_commission", "Total Comm."],
  ["ciboee_commission", "Ciboee 5%"],
  ["total_dc_collected", "Total DC Coll."],
  ["dc_payable", "DC Payable"],
  ["dc_loss", "DC Loss"],
  ["min_guarantee", "Min Guarantee"],
  ["other_expense", "Other Exp."],
];

export function MoneyCells({ row }) {
  return MONEY_COLS.map(([k]) => <Td key={k}>{formatINR(row[k])}</Td>);
}

export function ProfitCell({ value, testId }) {
  return (
    <Td className={`font-semibold ${profitTone(value)}`}>
      <span data-testid={testId}>{formatINR(value)}</span>
    </Td>
  );
}
